import { useMemo, useState } from 'react'
import { Screen } from '@/components/layout'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { FilterBar, type FilterOption } from '@/components/pickers'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList, SkeletonMetrics } from '@/components/states'
import { api } from '@/lib/api'
import { aggregateUnitState, useProseState } from '@/lib/analysis'
import { t } from '@/lib/copy'
import { formatMoney } from '@/lib/format'
import { Link, useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

type Filter = 'all' | 'quickWins' | 'cashFlow' | 'debt' | 'subscriptions' | 'fees' | 'savings' | 'done'

const FILTERS: FilterOption<Filter>[] = [
  { value: 'all', labelKey: 'recommendations.filter.all' },
  { value: 'quickWins', labelKey: 'recommendations.filter.quickWins' },
  { value: 'cashFlow', labelKey: 'recommendations.filter.cashFlow' },
  { value: 'debt', labelKey: 'recommendations.filter.debt' },
  { value: 'subscriptions', labelKey: 'recommendations.filter.subscriptions' },
  { value: 'fees', labelKey: 'recommendations.filter.fees' },
  { value: 'savings', labelKey: 'recommendations.filter.savings' },
  { value: 'done', labelKey: 'recommendations.filter.done' },
]

/** Prioritized actions, ranked by monthly saving against effort. */
export function RecommendationsScreen() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')

  const all = useAsync(() => api.recommendations.list(), [])
  const rows = all.state.data ?? []

  const visible = useMemo(() => {
    if (filter === 'done') return rows.filter((rec) => rec.status === 'done')
    const open = rows.filter((rec) => rec.status === 'open')
    if (filter === 'all') return open
    if (filter === 'quickWins') return open.filter((rec) => rec.effort === 'low')
    return open.filter((rec) => rec.theme === filter)
  }, [rows, filter])

  const open = rows.filter((rec) => rec.status === 'open')
  const currency = rows[0]?.monthlyImpact?.currency ?? 'USD'
  const potential = open.reduce((sum, rec) => sum + Math.abs(rec.monthlyImpact?.amount ?? 0), 0)

  const metrics: MetricSpec[] = [
    {
      key: 'potential',
      label: t('recommendations.metric.potential'),
      value: formatMoney({ amount: potential, currency }, { absolute: true }),
      tone: 'positive',
    },
  ]

  // Per-recommendation, so a card whose prose landed sits next to one still
  // being written instead of the whole list waiting — recommendation.schema.json
  // proseState. The cards render themselves either way.
  const prose = useProseState(aggregateUnitState(rows.map((rec) => rec.proseState)), all.reload)

  const dataState =
    all.state.status === 'error'
      ? 'error'
      : all.state.status === 'loading' && !all.state.data
        ? 'loading'
        : rows.length === 0
          ? 'empty'
          : open.length === 0
            ? 'allDone'
            : 'ready'

  // Ranking, impact figures and filters are deterministic and unaffected, so
  // `analysisPending` and `analysisFailed` are silent refinements of `ready`.
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // The three states that render the identical tree below. `analysisPending`
  // adds no spinner and disables nothing; `analysisFailed` is not the error
  // treatment. The only difference between them and `ready` is whether the
  // prose slots hold generated text or their copyKey — and they hold one
  // either way, so the layout does not move when prose arrives.
  // `allDone` joins them because it is an inline note above a live list, not
  // a replacement for the screen.
  const rendersContent =
    screenState === 'ready' ||
    screenState === 'allDone' ||
    screenState === 'analysisPending' ||
    screenState === 'analysisFailed'

  return (
    <Screen
      id="recommendations"
      state={screenState}
      actions={
        <Link screen="healthScore" className="btn btn-secondary">
          {t('healthScore.title')}
        </Link>
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonMetrics count={1} />
          <SkeletonList rows={6} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="recommendations.error.title"
          error={all.state.status === 'error' ? all.state.error : undefined}
          onRetry={all.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="recommendations.empty.title" />
      ) : rendersContent ? (
        <div className="stack">
          {screenState === 'allDone' ? <EmptyState titleKey="recommendations.allDone.title" /> : null}
          <MetricRow metrics={metrics} />
          <FilterBar options={FILTERS} value={filter} onChange={setFilter} label={t('recommendations.title')} />
          <div className="stack-sm">
            {visible.map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                onSelect={() => navigate('recommendationDetail', { id: rec.id })}
              />
            ))}
          </div>
        </div>
      ) : null}
    </Screen>
  )
}
